import React, { useEffect, useRef } from 'react';
import I18n from "i18n-js";
import { notification } from '@duxton-web/ui';
import { WarningOutlined } from '@duxton-web/icons';
import get from "lodash/get";
import has from "lodash/has";
import isEmpty from "lodash/isEmpty";
import { apiCaller, delDictLS, getDictLS, setDictLS } from '../../utils';

const DOWNLOAD_QUEUE = 'DOWNLOAD_QUEUE';
const DOWNLOAD_QUEUE_LOCK = 'DOWNLOAD_QUEUE_LOCK';

const REPROCESS_INTERVAL = 3000; // 3s
const STALE_DURATION = 5000; // 5s
const LOCK_DURATION = 30 * 60 * 1000; // 30m

const useBackgroundDownload = ({ trackTroyAnalytics = () => { } }) => {
  const timeouts = useRef([]);

  const removeItem = (id) => {
    delDictLS(DOWNLOAD_QUEUE, id);
    delDictLS(DOWNLOAD_QUEUE_LOCK, id);

    // dispatch event to notify download queue finish
    // e.g. user still stay on the component and download is finished
    window.dispatchEvent(new CustomEvent('downloadQueueFinish', { detail: { id } }));
  }
  const trackEvent = (data, status) => {
    if (!data.eventID || !data.eventAction || !data.eventParams || !data.startTime) return;
    trackTroyAnalytics(data.eventID, data.eventAction, { ...data.eventParams, LOADING_STATUS: status, TIME_TAKEN: new Date().getTime() - data.startTime });
  }

  const processItem = async (id) => {
    try {
      const now = new Date().getTime();

      // sanity check for expired lock
      const lock = getDictLS(DOWNLOAD_QUEUE_LOCK);
      if (!lock[id] || now - lock[id] > LOCK_DURATION) {
        removeItem(id);
        return
      };

      // sanity check for racing queue update & poll
      const queue = getDictLS(DOWNLOAD_QUEUE);
      if (!queue[id]) {
        removeItem(id);
        return
      };

      // update time to prevent lock's stale too long / tab close / tab crash
      setDictLS(DOWNLOAD_QUEUE, id, { ...queue[id], updateTime: now });

      const response = await apiCaller(REPROCESS_INTERVAL - 250).get(`/mex-insights/download/v1/generated-insights/${queue[id].refID}`);
      const status = get(response, ['data', 'status']);
      if (status === 'IN_PROGRESS') {
        const timeoutID = setTimeout(() => processItem(id), REPROCESS_INTERVAL);
        timeouts.current.push(timeoutID);

        return;
      } else if (status === 'SUCCESS') {
        notification.success({
          message: I18n.t("troyCommon-download-003-002"),
          description: I18n.t("troyCommon-download-003-001"),
        });
        window.open(response.data.s3_url, '_self');
        trackEvent(queue[id], "SUCCESS");
        removeItem(id);

        return
      }

      notification.warning({
        message: I18n.t("mex-insightsv2-040-001-heading"),
        description: I18n.t("mex-insightsv2-040-002-body"),
        icon: (<WarningOutlined style={{ color: "#E58D11" }} />),
      });

      trackEvent(queue[id], "FAILED");
      removeItem(id);
    } catch (error) {
      notification.warning({
        message: I18n.t("mex-insightsv2-040-001-heading"),
        description: I18n.t("mex-insightsv2-040-002-body"),
        icon: (<WarningOutlined style={{ color: "#E58D11" }} />),
      });

      removeItem(id);
    }
  }

  const processQueue = () => {
    const queue = getDictLS(DOWNLOAD_QUEUE);
    if (isEmpty(queue)) return

    for (const id in queue) {
      const item = queue[id];
      const now = new Date().getTime();

      const isStaleNotRun = now - item.startTime > STALE_DURATION;
      if (!item.refID && isStaleNotRun) {
        // remove staled item / due to tab close / crash
        removeItem(id);
        continue;
      }

      const lock = getDictLS(DOWNLOAD_QUEUE_LOCK);
      const isUnlock = !lock[id] || now - lock[id] > LOCK_DURATION;
      const isStaleRun = now - item.updateTime > STALE_DURATION;
      if (item.refID && (isUnlock || isStaleRun)) {
        // lock item to prevent racing
        setDictLS(DOWNLOAD_QUEUE_LOCK, id, now);

        // process item
        processItem(id);
      }
    }
  }

  const startQueue = (event) => {
    if (has(event, ['detail', 'id']) || has(event, ['detail', 'data', 'refID'])) return

    // prevent duplicate
    if (!isEmpty(getDictLS(DOWNLOAD_QUEUE, event.detail.id))) return

    setDictLS(DOWNLOAD_QUEUE, event.detail.id, { ...event.detail.data });

    processQueue();
  }

  useEffect(() => {
    // check on mount
    processQueue();

    // listen to download queue start event
    window.addEventListener('downloadQueueStart', startQueue);

    // in case of tab close / crash, other tab will take over
    const interval = setInterval(processQueue, STALE_DURATION - 250);

    return () => {
      if (interval) clearInterval(interval);

      if (timeouts.current.length) {
        timeouts.current.forEach(clearTimeout);
        timeouts.current = [];
      }

      window.removeEventListener('downloadQueueStart', startQueue);

      localStorage.removeItem(DOWNLOAD_QUEUE_LOCK);
    }
  }, []);

  return null
};

export default useBackgroundDownload;